import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import deLabels from 'react-phone-number-input/locale/de.json';
import enLabels from 'react-phone-number-input/locale/en.json';
import 'react-phone-number-input/style.css';
import { cn } from '../../utils/utils';

interface PhoneNumberFieldProps {
    value?: string;
    onChange?: (value: string) => void;
    label: string;
    /** Default country for the dropdown when no value is set yet.
     *  ISO 3166-1 alpha-2 code, e.g. 'DE'. Driven by the user's
     *  country selection so the dropdown opens on the right country. */
    defaultCountry?: string;
    className?: string;
    disabled?: boolean;
    /** Render the label as a separate block above the input rather than
     *  the floating-label-inside pattern. Avoids the country-dropdown
     *  overlap when the label would sit inside the input. */
    labelAbove?: boolean;
}

// Phone input wrapper matching the look of our InputField. Built on
// react-phone-number-input; the library handles country detection, E.164
// formatting, fixed-line vs mobile validation, and the country picker
// dropdown (with flags). We just style the chrome.
const PhoneNumberField = ({
    value,
    onChange,
    label,
    defaultCountry,
    className,
    disabled,
    labelAbove = false,
}: PhoneNumberFieldProps) => {
    const { i18n } = useTranslation();
    const [focused, setFocused] = useState(false);
    const isFloating = focused || !!value;

    const labels = i18n.language?.toLowerCase().startsWith('de') ? deLabels : enLabels;

    const phoneInput = (
        <PhoneInput
            international
            defaultCountry={(defaultCountry || undefined) as never}
            value={value || undefined}
            onChange={(next) => onChange?.(next ?? '')}
            labels={labels}
            disabled={disabled}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            numberInputProps={{
                className: cn(
                    'flex-1 outline-none bg-transparent text-foreground',
                    'placeholder:text-foreground/50',
                ),
            }}
            className={cn(
                'flex items-center gap-2 py-2 px-4 rounded-xl bg-transparent w-full',
                'border transition-all',
                focused ? 'border-primary' : 'border-foreground',
                disabled && 'cursor-not-allowed opacity-60',
            )}
        />
    );

    if (labelAbove) {
        return (
            <div className={cn('flex flex-col gap-1.5', className)}>
                <label className={'text-sm text-foreground/70 font-medium'}>
                    {label}
                </label>
                {phoneInput}
            </div>
        );
    }

    return (
        <div className={cn('relative', className)}>
            {phoneInput}
            <label
                className={cn(
                    'absolute left-3 pointer-events-none transition-all duration-200',
                    'truncate max-w-[calc(100%-1.5rem)]',
                    isFloating
                        ? '-top-2.5 text-xs bg-white px-1 text-foreground'
                        : 'top-1/2 -translate-y-1/2 text-sm text-foreground/50',
                )}
            >
                {label}
            </label>
        </div>
    );
};

export default PhoneNumberField;
